import type { TypographyVariantsOptions } from "@mui/material";

import { palette } from "./palette";

// When changing these, also update the Google Fonts <link> tags in:
// - apps/triton/src/index.html
// - .storybook/preview-head.html

const SPACING = 8;

/**
 * These options override the MUI default theme
 * {@link https://material-ui.com/customization/default-theme/}
 */

export const themeOptions = {
  // Design icon-size scale (Figma icon sizes), rem at 16px base. The MUI SvgIcon
  // keyword sizes derive from these (see material-ui/components/svg-icon.ts), so
  // `<Icon fontSize="small" />` and `styled(Icon)({ fontSize: theme.iconSize.sm })`
  // are the same value. `xs` is 16 (Figma's 18 lowered to fit real inline icons);
  // `sm`/`md` match MUI defaults, `lg` overrides MUI's 35 → 36.
  iconSize: {
    xs: "1rem", // 16px
    sm: "1.25rem", // 20px
    md: "1.5rem", // 24px
    lg: "2.25rem", // 36px
  },

  layout: {
    NAVBAR_HEIGHT: 7,
    CATALYST_WIDTH: 55,
    PROJECT_TOOLBAR_HEIGHT: 10,
    BRANCH_INDICATOR_HEIGHT: 1.5,
    BODY_MARGIN: 5,
    SCREEN_BREAK_1: 150,
    SCREEN_BREAK_2: 192,
    SCREEN_BREAK_3: 240,
    SCREEN_BREAK_4: 320,
  },

  palette,

  shape: {
    borderRadius: 4,
    borderRadiusMd: 8,
    borderRadiusLg: 16,
  },

  sizing: (factor: number) => factor * SPACING,

  spacing: SPACING,

  transitions: {
    duration: {
      shortest: 200,
      short: 250,
      standard: 300,
      complex: 375,
    },
  },

  typography: {
    fontFamily: "'Barlow', sans-serif",
    fontFamilyMono: "'IBM Plex Mono', monospace",
    // Design weight scale: 300 / 400 / 500 / 600 — the weights loaded for Barlow
    // (see the Google Fonts <link> referenced above). 600 is the heaviest weight
    // in the scale and maps to `fontWeightBold`; 700+ are out of scale.
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    // Design size scale (Figma `font/size`), rem at 16px base; px noted inline.
    // Flat `fontSize<Step>` keys mirror the `fontWeight*` / `fontFamilyMono`
    // pattern (MUI's own `<property><Modifier>` idiom). A t-shirt ramp anchored on
    // `md` = 14px (body): `sm`/`lg` are the ±2px neighbours, then up. Figma's
    // `2xs` (10.5px) and the old 48px top are omitted — both unused; re-add if a
    // design needs them. (MUI's base `typography.fontSize` numeric is left alone.)
    fontSizeXs: "0.6875rem", // 11px
    fontSizeSm: "0.75rem", // 12px
    fontSizeMd: "0.875rem", // 14px
    fontSizeLg: "1rem", // 16px
    fontSizeXl: "1.25rem", // 20px
    fontSizeXxl: "1.5rem", // 24px
    fontSizeXxxl: "2rem", // 32px
  } as const satisfies TypographyVariantsOptions,

  zIndex: {
    appBar: 1100,
    catalyst: 1300,
    drawer: 1350,
    // help-scout beacon is 1500
    snackbar: 1600,
    modal: 1700,
    tooltip: 1800,
  },
} as const;

declare module "@mui/material" {
  // rem only for now — add "px" to the union if we ever need it.
  type FontSize = `${number}rem`;

  interface Theme {
    iconSize: typeof themeOptions.iconSize;
    layout: typeof themeOptions.layout;
    shape: typeof themeOptions.shape;
    sizing: typeof themeOptions.sizing;
    zIndex: typeof themeOptions.zIndex;
  }

  // Allow `fontSize="xs"` on icons (custom size below MUI's `small`).
  interface SvgIconPropsSizeOverrides {
    xs: true;
  }

  interface TypographyVariants {
    fontFamilyMono: string;
    fontSizeXs: FontSize;
    fontSizeSm: FontSize;
    fontSizeMd: FontSize;
    fontSizeLg: FontSize;
    fontSizeXl: FontSize;
    fontSizeXxl: FontSize;
    fontSizeXxxl: FontSize;
  }

  interface TypographyVariantsOptions {
    fontFamilyMono?: string;
    fontSizeXs?: FontSize;
    fontSizeSm?: FontSize;
    fontSizeMd?: FontSize;
    fontSizeLg?: FontSize;
    fontSizeXl?: FontSize;
    fontSizeXxl?: FontSize;
    fontSizeXxxl?: FontSize;
  }
}
